import React, {
	useEffect,
	useMemo,
	useCallback,
	memo,
	useState,
	useLayoutEffect,
} from "react";
import { useTranslation } from "react-i18next";
import { LatLngLiteral } from "leaflet";

import {
	Archive,
	ValueToggle,
} from "../../../../redux/reducers/Archives/interface";
import { Popup, StyledColumn, StyledRow, Text } from "../../../common";
import { PlayerControl } from "../PlayerControl";
import { useAppContext } from "../../../../context";
import { ConstantFormat } from "../../../../utils/DateFns";
import { ConstantLanguage } from "../../../../assets/languages/langs";
import { ExecutorStatus } from "../../../../services/Executor";
import { upperCaseFirst } from "../../../../utils";

const PlayerBase: React.FC<PlayerBase.Props> = ({
	data,
	player,
	setPlayerToggle,
	setPlayerPosition,
	setPlayerMax,
	setPlayerNext,
	setPlayerDate,
	setPlayerSpeed,
	setPlayerDuration,
	setPlayerRun,
	popoverProps,
	closedTime,
}) => {
	const { t } = useTranslation();
	const { dateFns, language } = useAppContext();
	const [time, setTime] = useState(60_000);
	const [item, setItem] = useState<{
		start?: Archive.SegmentPlayer;
		end?: Archive.SegmentPlayer;
		orderStart: number;
		orderEnd: number;
		segmentStart?: Archive.SegmentPlayer;
		segmentEnd?: Archive.SegmentPlayer;
	}>({
		start: undefined,
		end: undefined,
		orderEnd: 0,
		orderStart: 0,
		segmentStart: undefined,
		segmentEnd: undefined,
	});

	const transText = useMemo(
		() => ({
			busy: t("orderPageWidgets.parking.parkingTable.str202"),
			dinner: t("orderPageWidgets.parking.parkingTable.str203"),
			home: t("orderPageWidgets.parking.parkingTable.str204"),
			on_order: t("orderPageWidgets.parking.parkingTable.str205"),
			available: t("orderPageWidgets.parking.parkingTable.str206"),
			closed: t("orderPageWidgets.parking.parkingTable.str207"),
			ownOrder: t("orderPageWidgets.parking.parkingTable.str208"),
			debt: t("orderPageWidgets.executors.str206"),

			speed: t(`speed`),
			accuracy: t(`accuracy`),
			altitude: t(`altitude`),
			mpsUnit: t(`units.mps`),
			kphUnit: t(`units.kph`),
			meter: t(`units.meter`),

			startOrder: upperCaseFirst(t("mapGPS.str207")),
			orderStatus: `${upperCaseFirst(t(`status`))}: `,
			point: `${upperCaseFirst(t("point"))}: `,
			date: `${upperCaseFirst(t(`date`))}: `,
			durationMS: `${upperCaseFirst(t("mapGPS.str202"))}: `,
			registrationNumber: `${upperCaseFirst(t("mapGPS.str203"))}: `,
			carNumber: `${upperCaseFirst(t("mapGPS.str205"))}: `,
			executorCallSign: `${upperCaseFirst(t("mapGPS.str206"))}: `,
			executorStatus: `${upperCaseFirst(t("mapGPS.str208"))}: `,
			start: `${upperCaseFirst(t(`mapGPS.str200`))}: `,
			end: `${upperCaseFirst(t("mapGPS.str201"))}: `,
		}),
		[t],
	);

	const getStatus = useCallback(
		(status) => {
			if (!status) return "";

			if (status === ExecutorStatus.BUSY) {
				return transText.busy;
			}

			if (status === ExecutorStatus.DINNER) {
				return transText.dinner;
			}

			if (status === ExecutorStatus.HOME) {
				return transText.home;
			}

			if (status === ExecutorStatus.ON_ORDER) {
				return transText.on_order;
			}

			return transText[status] || status;
		},
		[transText],
	);

	const formatTime = useMemo(
		() => ({
			full:
				language === ConstantLanguage.EN
					? ConstantFormat.MDYHMS
					: ConstantFormat.MDYHMS_UK,
			minutes:
				language === ConstantLanguage.EN
					? ConstantFormat.HMS
					: ConstantFormat.HMS_UK,
			day:
				language === ConstantLanguage.EN
					? ConstantFormat.MDY
					: ConstantFormat.MDY_UK,
		}),
		[language],
	);

	const converterLine = useCallback(
		(params: {
			end: Archive.SegmentPlayer;
			start: Archive.SegmentPlayer;
			segmentStart?: Archive.SegmentPlayer;
			segmentEnd?: Archive.SegmentPlayer;
			orderStartTime: number;
			orderEndTime: number;
		}): [string, string][] => {
			const {
				end,
				start,
				segmentStart,
				segmentEnd,
				orderStartTime = 0,
				orderEndTime = 0,
			} = params;
			const mps = `${end.speed?.toFixed(2) || 0} ${transText.mpsUnit}`;
			const kph = `${(end.speed * 3.6)?.toFixed(2) || 0} ${
				transText.kphUnit
			}`;
			const durationMS = dateFns.formatDistance(
				end.date,
				segmentStart?.date || start.date,
				language,
			);
			const dataPoint = dateFns.formatTime2(
				new Date(end.date),
				formatTime.full,
				{
					lang: language,
					timezone: dateFns.timezone,
				},
			);

			const existOrder = end?.order || start?.order;

			const order: any[] = existOrder
				? [
						[`- ${transText.startOrder}`, ` -`],
						[
							transText.orderStatus ?? "",
							existOrder?.executingStage
								? t(
										`order_executing_stages.${existOrder.executingStage}`,
								  ) || existOrder?.executingStage
								: "",
						],
						[
							transText.start,
							orderStartTime
								? dateFns.formatTime2(
										new Date(orderStartTime),
										formatTime.full,
										{
											lang: language,
											timezone: dateFns.timezone,
										},
								  )
								: "",
						],
						[
							transText.end ?? "",
							(orderEndTime && !end.order && start?.order) ||
							(closedTime && end.date === segmentEnd?.date)
								? dateFns.formatTime2(
										new Date(closedTime || orderEndTime),
										formatTime.full,
										{
											lang: language,
											timezone: dateFns.timezone,
										},
								  )
								: "",
						],
						[
							transText.durationMS ?? "",
							orderStartTime === start.date ||
							!orderStartTime ||
							!end.date
								? ""
								: dateFns.formatDistance(
										orderStartTime,
										end.date,
										language,
								  ),
						],
						[`-`, ``],
				  ]
				: [];

			return [
				[transText.point ?? "", dataPoint],
				[
					transText.start ?? "",
					dateFns.formatTime2(
						new Date(segmentStart?.date || start.date),
						formatTime.minutes,
						{
							lang: language,
							timezone: dateFns.timezone,
						},
					),
				],

				[
					transText.end ?? "",
					dateFns.formatTime2(
						new Date(segmentEnd?.date || end.date),
						formatTime.minutes,
						{
							lang: language,
							timezone: dateFns.timezone,
						},
					),
				],

				[
					transText.durationMS ?? "",
					end.date === start.date ? "" : durationMS,
				],

				...order,
				[
					transText.registrationNumber ?? "",
					end?.car?.registrationNumber || "",
				],
				[transText.carNumber ?? "", end?.carNumber || ""],
				[transText.executorCallSign ?? "", end?.executorCallSign],
				[
					transText.executorStatus ?? "",
					getStatus(end.executor.status),
				],
				[transText.speed, `${kph} (${mps})`],
				[
					transText.altitude,
					`${end.altitude?.toFixed(2) || 0} ${transText.meter}`,
				],
				[
					transText.accuracy,
					`${end.accuracy?.toFixed(2) || 0} ${transText.meter}`,
				],
			];
		},
		[
			transText.mpsUnit,
			transText.kphUnit,
			transText.startOrder,
			transText.orderStatus,
			transText.start,
			transText.end,
			transText.durationMS,
			transText.point,
			transText.registrationNumber,
			transText.carNumber,
			transText.executorCallSign,
			transText.executorStatus,
			transText.speed,
			transText.altitude,
			transText.meter,
			transText.accuracy,
			dateFns,
			language,
			formatTime.full,
			formatTime.minutes,
			t,
			closedTime,
			getStatus,
		],
	);

	const {
		lookupMapKeys,
		pointLookupMap,
		size,
		ordersStartMap,
		ordersEndMap,
		segmentStartMap,
		segmentEndMap,
	} = useMemo<PlayerBase.PlayerPoints>(() => {
		const lookup: PlayerBase.PlayerPoints["pointLookupMap"] = new Map();
		const segmentStart: Map<number, Archive.SegmentPlayer> = new Map();
		const segmentEnd: Map<number, Archive.SegmentPlayer> = new Map();
		const ordersStart: Map<number, number> = new Map();
		const ordersEnd: Map<number, number> = new Map();

		data.forEach(({ segments }) => {
			segments?.forEach((segment) => {
				const start = segment.at(0);
				const end = segment.at(-1);
				segment?.forEach((item) => {
					if (item.date) {
						const orderId = item?.order?.id;
						if (start) segmentStart.set(item.date, start);
						if (end) segmentEnd.set(item.date, end);
						lookup.set(item.date, item);
						if (orderId) {
							if (!ordersStart.has(orderId)) {
								ordersStart.set(orderId, item.date);
							}

							if (!ordersEnd.has(orderId)) {
								const orderSegment = segment.filter(
									(s) => s.order?.id,
								);
								const end = orderSegment.at(-1);
								ordersEnd.set(orderId, end?.date || item.date);
							}
						}
					}
				});
			});
		});

		const keys = Array.from(lookup.keys()).sort();

		return {
			pointLookupMap: lookup,
			lookupMapKeys: keys,
			size: lookup.size,
			ordersStartMap: ordersStart,
			ordersEndMap: ordersEnd,
			segmentStartMap: segmentStart,
			segmentEndMap: segmentEnd,
		};
	}, [data]);

	useEffect(() => {
		if (setPlayerMax) setPlayerMax(size);
	}, [size, setPlayerMax]);

	const onChangeIsPlaying = useCallback(
		(payload: boolean) => {
			if (payload) setPlayerToggle("on");
			else setPlayerToggle("off");
		},
		[setPlayerToggle],
	);

	const onChangePosition = useCallback(
		(next: number) => {
			const nextKey = lookupMapKeys[next];
			const nextItem = pointLookupMap.get(nextKey);
			const prevKey = lookupMapKeys[next - 1];
			const prevItem = pointLookupMap.get(prevKey);

			const date = nextItem?.date || nextItem?.date;
			if (date) {
				const orderId = nextItem?.order?.id || prevItem?.order?.id;
				const orderStart = orderId
					? ordersStartMap.get(orderId) ?? 0
					: 0;
				const orderEnd = orderId ? ordersEndMap.get(orderId) ?? 0 : 0;
				const segmentStart = segmentStartMap.get(date);
				const segmentEnd = segmentEndMap.get(date);
				setItem({
					start: prevItem,
					end: nextItem,
					orderStart,
					orderEnd,
					segmentStart,
					segmentEnd,
				});
			}

			if (nextItem && prevItem) {
				setPlayerPosition(prevItem.point);
				setPlayerNext(nextItem.point);
				setPlayerDate(nextItem.date);
				const durationTime = nextItem.date - prevItem.date;
				setTime(Math.max(durationTime, 0));
				setPlayerDuration(Math.max(durationTime / 1000, 0));
			}
		},

		[
			lookupMapKeys,
			pointLookupMap,
			ordersStartMap,
			ordersEndMap,
			segmentStartMap,
			segmentEndMap,
			setPlayerPosition,
			setPlayerNext,
			setPlayerDate,
			setPlayerDuration,
		],
	);

	useLayoutEffect(() => {
		if (!player.next || !player.position) {
			onChangePosition(1);
		}
	}, [onChangePosition, player.position, player.next]);

	const onChangeSpeed = useCallback(
		(payload: number) => {
			setPlayerSpeed(payload);
		},
		[setPlayerSpeed],
	);

	const onChangeRun = useCallback(
		(payload: boolean) => {
			if (payload) setPlayerRun("off");
			else setPlayerRun("on");
		},
		[setPlayerRun],
	);

	const popoverElement = useMemo(() => {
		if (!item) return <></>;
		if (!item.end || !item.start) return <></>;
		const list: [string, string][] = converterLine({
			end: item.end,
			start: item.start,
			segmentStart: item.segmentStart,
			segmentEnd: item.segmentEnd,
			orderStartTime: item.orderStart,
			orderEndTime: item.orderEnd,
		});
		return (
			<StyledColumn>
				{list.length &&
					list.map((value, j) => {
						if (!value[0]) return <></>;
						return (
							<Text
								text={value[0]}
								value={value[1]}
								key={`${j + 1}`}
							/>
						);
					})}
			</StyledColumn>
		);
	}, [converterLine, item]);

	return (
		<StyledRow position="relative" w="100%">
			<StyledRow
				position="absolute"
				w="100%"
				zi={500}
				bgC="rgba( 0, 0, 0, .3 )"
			>
				<PlayerControl
					isPlaying={player.toggle}
					position={0}
					max={player.max}
					time={time}
					onChangePosition={onChangePosition}
					onChangeIsPlaying={onChangeIsPlaying}
					onChangeSpeed={onChangeSpeed}
					onChangeRun={onChangeRun}
					popoverProps={popoverProps}
				>
					{popoverElement}
				</PlayerControl>
			</StyledRow>
		</StyledRow>
	);
};

declare namespace PlayerBase {
	interface Props {
		data: Archive.LocationPlayer[];
		player: Archive.Player;
		setPlayerRun: (data: ValueToggle) => void;
		setPlayerToggle: (data: ValueToggle) => void;
		setPlayerPosition: (data: LatLngLiteral) => void;
		setPlayerMax: (data: number) => void;
		setPlayerNext: (data: LatLngLiteral) => void;
		setPlayerDate: (data: number) => void;
		setPlayerSpeed: (data: number) => void;
		setPlayerDuration: (data: number) => void;
		popoverProps?: Popup.Props;
		closedTime?: string | Date | number;
	}

	interface PlayerPoints {
		pointLookupMap: Map<number, Archive.SegmentPlayer>;
		lookupMapKeys: number[];
		size: number;
		ordersStartMap: Map<number, number>;
		ordersEndMap: Map<number, number>;
		segmentStartMap: Map<number, Archive.SegmentPlayer>;
		segmentEndMap: Map<number, Archive.SegmentPlayer>;
	}
}

export const Player = memo(PlayerBase);
export default PlayerBase;
